import { useContext } from 'react'
import { generatePath } from 'react-router-dom'
import { CompanyTag } from '@/components/ui/badge'
import { GridCellProps, GridColumnProps } from '@progress/kendo-react-grid'
import { PermissionsEnum } from 'api/Identity/Types/PermissionsEnum'
import { TransactionDto, TransactionKey } from 'api/Transaction/Types/Transaction'
import { Link } from 'components/Link'
import { generateClassNameForReverseType } from 'helpers/transactions'
import { AuthSessionContext } from 'utils/AuthContext'
import { formatLocalDateUTC } from 'utils/date'
import { links } from 'utils/featureToggles'
import { isEmpty } from 'utils/is-empty'
import { hasPermission } from 'utils/permissionsValidator'
import { getCreditCreditOrFirstOneWallet, getCreditWalletByDirectionType } from 'utils/wallet'

import '../Create/transaction.scss'

export interface GridColumn extends GridColumnProps {
  show: boolean
  width?: number
}

const ReferenceCell = (props: GridCellProps): JSX.Element => {
  const [auth] = useContext(AuthSessionContext)
  const permissions = auth?.permissions || []

  if (!hasPermission(permissions, PermissionsEnum.CanViewCustomUserWalletTransaction)) {
    return (
      <td className={generateClassNameForReverseType(props.dataItem)}>
        {props.dataItem.referenceId}
      </td>
    )
  } else {
    return (
      <td
        className={generateClassNameForReverseType(props.dataItem)}
        style={{ color: 'var(--link)', textDecoration: 'underline' }}
      >
        <Link
          to={{
            pathname: generatePath('/transactions/:id', {
              id: props.dataItem.id,
            }),
          }}
        >
          {props.dataItem.referenceId}
        </Link>
      </td>
    )
  }
}

const walletSingleAndMultiTransactionColumn: GridColumn[] = [
  {
    title: 'Reference',
    field: TransactionKey.reference,
    cell: ReferenceCell,
    show: true,
    className: 'table-columns__content',
    filter: 'text',
  },
  {
    title: 'Type',
    field: TransactionKey.type,
    cell: (props: GridCellProps): JSX.Element => {
      const trans: TransactionDto = props.dataItem
      // id 680 means reversed
      // if (trans.type.id === 680) {
      //     return <td className={classNames('columns__content', 'transaction-reversed')}>{props.dataItem.type.name}</td>
      // }
      return <td className={generateClassNameForReverseType(trans)}>{trans.type.name}</td>
    },
    show: true,
    className: 'transaction-columns__content',
    filter: 'text',
  },
  {
    title: 'Client',
    field: TransactionKey.walletTransactionWalletClient,
    cell: (props: GridCellProps): JSX.Element => {
      const data: TransactionDto = props.dataItem
      if (isEmpty(props.dataItem.walletTransactions)) {
        return <td className={generateClassNameForReverseType(props.dataItem)} />
      }

      const walletTransaction = getCreditCreditOrFirstOneWallet(data.walletTransactions)
      const client = walletTransaction?.wallet?.client
      if (!client) {
        return <td className={generateClassNameForReverseType(props.dataItem)} />
      }

      return (
        <td
          className={generateClassNameForReverseType(props.dataItem)}
          style={{ color: 'var(--link)', textDecoration: 'underline' }}
        >
          <Link
            state={{ redirectTo: links.transactionWallet.url }}
            to={{
              pathname: generatePath(links.recordsClients.detailsUrl as string, {
                id: client.id,
                slug: 'details',
              }),
            }}
          >
            {client.name}
          </Link>
        </td>
      )
    },
    show: true,
    filter: 'text',
  },
  {
    title: 'Wallet',
    field: TransactionKey.walletTransactionWallet,
    cell: (props: GridCellProps): JSX.Element => {
      const data: TransactionDto = props.dataItem
      if (isEmpty(props.dataItem.walletTransactions)) {
        return <td className={generateClassNameForReverseType(props.dataItem)} />
      }

      const walletTransaction = getCreditCreditOrFirstOneWallet(data.walletTransactions)
      const wallet = walletTransaction?.wallet
      if (!wallet) {
        return <td className={generateClassNameForReverseType(props.dataItem)} />
      }

      return (
        <td
          className={generateClassNameForReverseType(props.dataItem)}
          style={{ color: 'var(--link)', textDecoration: 'underline' }}
        >
          <Link
            state={{ redirectTo: links.transactionWallet.url }}
            to={{
              pathname: generatePath(links.recordsWallets.detailsUrl as string, {
                id: wallet.id,
                slug: 'details',
              }),
            }}
          >
            {wallet.name}
          </Link>
        </td>
      )
    },
    resizable: true,
    show: true,
    filter: 'text',
  },
  {
    title: 'Amount',
    field: TransactionKey.walletTransactionAmount,
    cell: (props: GridCellProps): JSX.Element => {
      const data: TransactionDto = props.dataItem
      if (isEmpty(props.dataItem.walletTransactions)) {
        return <td className={generateClassNameForReverseType(props.dataItem)} />
      }

      const walletTransaction = getCreditCreditOrFirstOneWallet(data.walletTransactions)
      const amount = walletTransaction?.amount.toFixed(2)

      return <td className={generateClassNameForReverseType(props.dataItem, 'number')}>{amount}</td>
    },
    resizable: true,
    show: true,
    filter: 'text',
  },
  {
    title: 'Currency',
    field: TransactionKey.walletTransactionWalletCurrency,
    cell: (props: GridCellProps): JSX.Element => {
      const data: TransactionDto = props.dataItem
      if (isEmpty(props.dataItem.walletTransactions)) {
        return <td className={generateClassNameForReverseType(props.dataItem)} />
      }

      return (
        <td className={generateClassNameForReverseType(props.dataItem, 'number')}>
          {getCreditCreditOrFirstOneWallet(data.walletTransactions)?.wallet.currency.id}
        </td>
      )
    },
    resizable: true,
    show: true,
    filter: 'text',
  },
  {
    title: 'Status',
    field: TransactionKey.state,
    cell: (props: GridCellProps) => (
      <td className={generateClassNameForReverseType(props.dataItem)}>
        {props.dataItem.state.name}
      </td>
    ),
    className: 'transaction-columns__content',
    show: true,
    filter: 'text',
  },
  {
    title: 'Entity',
    field: TransactionKey.walletTransactionWalletCompany,
    cell: (props: GridCellProps): JSX.Element => {
      const data: TransactionDto = props.dataItem
      if (!props?.dataItem?.walletTransactions) {
        return <td className={generateClassNameForReverseType(props.dataItem)} />
      }

      const directionType = data.walletTransactions?.[0]?.id
      const walletTransaction = getCreditWalletByDirectionType(
        data.walletTransactions,
        Number.parseInt(directionType, 10)
      )
      const company = walletTransaction?.wallet?.company
      if (!company) {
        return <td className={generateClassNameForReverseType(props.dataItem)} />
      }

      return (
        <td className={generateClassNameForReverseType(props.dataItem)}>
          <CompanyTag id={company.id}>{company.name}</CompanyTag>
        </td>
      )
    },
    resizable: true,
    show: true,
    filter: 'text',
    width: 80,
    minResizableWidth: 80,
  },
  {
    title: 'Date Created',
    field: TransactionKey.createdDate,
    cell: (props: GridCellProps) => (
      <td className={generateClassNameForReverseType(props.dataItem, 'date')}>
        {formatLocalDateUTC(props.dataItem.createdDate, true)}
      </td>
    ),
    show: true,
    filter: 'text',
  },
  {
    title: 'Date Updated',
    field: TransactionKey.updatedDate,
    cell: (props: GridCellProps) => (
      <td className={generateClassNameForReverseType(props.dataItem, 'date')}>
        {formatLocalDateUTC(props.dataItem.updatedDate, true)}
      </td>
    ),
    show: true,
    resizable: true,
    filter: 'text',
  },
]

export default walletSingleAndMultiTransactionColumn
